/**
 * Auth Resolution Module
 *
 * Handles resolving authentication for requests, including inheritance from collections.
 */

import type { HttpRequest, Collection, CollectionAuth } from '../../stores';

/**
 * The source of the resolved authentication
 */
export type AuthSource = 'request' | 'collection' | 'none';

/**
 * Result of auth resolution
 */
export interface ResolvedAuth {
  /** The auth configuration to use */
  auth: HttpRequest['auth'] | CollectionAuth | undefined;
  /** Where the auth came from */
  source: AuthSource;
}

/**
 * Resolves the effective authentication for a request.
 * If the request has 'inherit' auth type, uses the collection's auth.
 * Otherwise uses the request's own auth.
 *
 * @param request - The HTTP request
 * @param collection - The parent collection (if any)
 * @returns The resolved auth configuration
 */
export function resolveAuth(
  request: HttpRequest,
  collection?: Collection | null
): ResolvedAuth {
  // If request has explicit auth (not 'inherit' or 'none'), use it
  if (request.auth?.type && request.auth.type !== 'inherit' && request.auth.type !== 'none') {
    return {
      auth: request.auth,
      source: 'request',
    };
  }

  // If request explicitly has 'none', return no auth
  if (request.auth?.type === 'none') {
    return {
      auth: undefined,
      source: 'none',
    };
  }

  // If request has 'inherit' or no auth defined, try to use collection auth
  if (collection?.auth?.type && collection.auth.type !== 'none') {
    return {
      auth: collection.auth,
      source: 'collection',
    };
  }

  // No auth configured anywhere
  return {
    auth: undefined,
    source: 'none',
  };
}

/**
 * Gets a human-readable description of where the auth is coming from.
 *
 * @param request - The HTTP request
 * @param collection - The parent collection (if any)
 * @returns Object with source and auth type description
 */
export function getAuthSource(
  request: HttpRequest,
  collection?: Collection | null
): { source: AuthSource; authType: string | null } {
  const resolved = resolveAuth(request, collection);

  if (resolved.source === 'none') {
    return { source: 'none', authType: null };
  }

  return {
    source: resolved.source,
    authType: resolved.auth?.type || null,
  };
}

/**
 * Checks if a request is using inherited auth from its collection.
 *
 * @param request - The HTTP request
 * @param collection - The parent collection (if any)
 * @returns True if using inherited auth
 */
export function isUsingInheritedAuth(
  request: HttpRequest,
  collection?: Collection | null
): boolean {
  // Check if request explicitly inherits or has no auth set
  const isInheriting = request.auth?.type === 'inherit' || !request.auth?.type;

  // And collection has valid auth
  const collectionHasAuth = collection?.auth?.type && collection.auth.type !== 'none';

  return isInheriting && !!collectionHasAuth;
}

/**
 * Gets the auth type label for display purposes.
 *
 * @param authType - The auth type string
 * @returns Human-readable label
 */
export function getAuthTypeLabel(authType: string | undefined | null): string {
  if (!authType) return 'No Auth';

  const labels: Record<string, string> = {
    none: 'No Auth',
    inherit: 'Inherit from Collection',
    bearer: 'Bearer Token',
    basic: 'Basic Auth',
    'api-key': 'API Key',
    oauth2: 'OAuth 2.0',
    hawk: 'Hawk',
  };

  return labels[authType] || authType;
}
